import ToggleRow from '@components/Customization/ToggleRow';
import { useLocalization } from '@lib/localization';
import { Box, CardContent, Paper } from '@mui/material';
import { useGetConfigQuery, useUpdateConfigMutation } from '@store/api/rootEndpoints';
import { usePermission } from '@store/hooks';
import { type FC } from 'react';
import { CardDescription, CardHeader, CardTitle } from './ServerConfig.styled';

/**
 * What this server asks of the devices that connect to it. The switch saves at once and
 * sends `devices` alone, apart from the page's Save, as the languages drawer saves its part.
 *
 * It shows the stored value and keeps no copy of its own. It is disabled while its save is on
 * its way, since a second save of the config while one is running fails, and while the config
 * is read again after it, since until then it still shows the old value.
 */
const DevicesCard: FC = () => {
  const { t } = useLocalization();
  const { can } = usePermission();
  const { data: config, isFetching } = useGetConfigQuery();
  const [updateConfig, { isLoading }] = useUpdateConfigMutation();

  return (
    <Paper sx={{ mb: 2 }}>
      <CardHeader>
        <Box>
          <CardTitle variant="h6">{t('ServerConfig_Devices')}</CardTitle>
          <CardDescription variant="body2">{t('ServerConfig_Devices_Description')}</CardDescription>
        </Box>
      </CardHeader>
      <CardContent style={{ padding: '24px' }}>
        <ToggleRow
          label={t('ServerConfig_Devices_ServerOnlyMode')}
          description={t('ServerConfig_Devices_ServerOnlyMode_Description')}
          checked={config?.devices.serverOnlyMode ?? false}
          onChange={(serverOnlyMode) => void updateConfig({ devices: { serverOnlyMode } })}
          disabled={!can('server_config:put') || !config || isLoading || isFetching}
        />
      </CardContent>
    </Paper>
  );
};

export default DevicesCard;
